import React from 'react';
import DataTable from './DataTable';
import { Package, FileText, User, DollarSign, Calendar } from 'lucide-react';

const DataTableDemo = () => {
  // Sample data
  const sampleData = [
    {
      id: 1,
      name: 'Laptop Dell XPS 13',
      type: 'IT Equipment',
      status: 'Active',
      assignedTo: 'John Doe',
      value: 1200,
      date: '2024-01-15'
    },
    {
      id: 2,
      name: 'Office Chair',
      type: 'Furniture',
      status: 'Active',
      assignedTo: 'Jane Smith',
      value: 350,
      date: '2024-01-20'
    },
    {
      id: 3,
      name: 'Printer HP LaserJet',
      type: 'IT Equipment',
      status: 'Under Maintenance',
      assignedTo: 'Mike Johnson',
      value: 450,
      date: '2024-01-25'
    },
    {
      id: 4,
      name: 'Conference Table',
      type: 'Furniture',
      status: 'Active',
      assignedTo: 'Sarah Wilson',
      value: 800,
      date: '2024-02-01'
    },
    {
      id: 5,
      name: 'Desktop Computer',
      type: 'IT Equipment',
      status: 'Inactive',
      assignedTo: 'Tom Brown',
      value: 900,
      date: '2024-02-05'
    }
  ];

  // Column definitions
  const columns = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
      render: (value) => (
        <div className="d-flex align-items-center gap-2">
          <Package size={16} style={{ color: 'var(--primary-color)' }} />
          <strong>{value}</strong>
        </div>
      )
    },
    {
      key: 'name',
      label: 'Name',
      render: (value) => (
        <div className="fw-medium">{value}</div>
      )
    },
    {
      key: 'type',
      label: 'Type'
    },
    {
      key: 'status',
      label: 'Status',
      type: 'badge',
      badgeClass: (value) => {
        const statusClasses = {
          'Active': 'badge-success',
          'Under Maintenance': 'badge-warning',
          'Inactive': 'badge-secondary'
        };
        return statusClasses[value] || 'badge-secondary';
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      render: (value) => (
        <div className="d-flex align-items-center gap-2">
          <User size={14} style={{ color: 'var(--text-muted)' }} />
          {value}
        </div>
      )
    },
    {
      key: 'value',
      label: 'Value',
      type: 'currency',
      render: (value) => (
        <div className="d-flex align-items-center gap-2">
          <DollarSign size={14} style={{ color: 'var(--success-color)' }} />
          ${value.toLocaleString()}
        </div>
      )
    },
    {
      key: 'date',
      label: 'Date',
      type: 'date',
      render: (value) => (
        <div className="d-flex align-items-center gap-2">
          <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
          {new Date(value).toLocaleDateString()}
        </div>
      )
    }
  ];

  const handleRowClick = (row) => {
    //console.log('Row clicked:', row);
    alert(`Clicked on: ${row.name}`);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>DataTable Component Demo</h1>
          <p className="text-muted mb-0">Demonstration of the reusable DataTable component with all features</p>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <DataTable
            data={sampleData}
            columns={columns}
            title="Sample Assets"
            searchable={true}
            sortable={true}
            exportable={true}
            pagination={true}
            pageSize={3}
            showActions={true}
            onRowClick={handleRowClick}
            searchPlaceholder="Search assets..."
            emptyMessage="No assets found"
          />
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5>DataTable Features</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6>Core Features:</h6>
                  <ul>
                    <li>✅ Pagination with customizable page size</li>
                    <li>✅ Column sorting (ascending/descending)</li>
                    <li>✅ Global search across all columns</li>
                    <li>✅ Excel export functionality</li>
                    <li>✅ Row selection (single/multiple)</li>
                    <li>✅ Custom column rendering</li>
                    <li>✅ Responsive design</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Column Types:</h6>
                  <ul>
                    <li>✅ Text (default)</li>
                    <li>✅ Badge with custom styling</li>
                    <li>✅ Currency formatting</li>
                    <li>✅ Date formatting</li>
                    <li>✅ Custom render functions</li>
                    <li>✅ Icons and complex layouts</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTableDemo;
